import { AuthenticationScheme } from '@see-power-pft-common-js/auth/enums';
import { AuthStoreAccess } from 'common-react/auth/auth-store';
import { AuthActionHooks, IAuthScheme } from 'common-react/auth/AuthProvider';
import {
  getJwtTokensFromOidcUser,
  getSimaasSchemeAuthenticationJwtTokenUsingAccessToken,
  getUserManager,
} from 'common-react/auth/schemes/depsso/depsso';
import { UserManager } from 'oidc-client-ts';
import { useCallback, useEffect, useMemo } from 'react';

const useDebugLogging = false;

const processedStates = new Set<string>();

const useDepSsoCallbackHandling = (
  userManager: UserManager,
  props: { authStoreAccess: AuthStoreAccess; actionHooks: AuthActionHooks },
) => {
  useEffect(() => {
    if (
      props.authStoreAccess.getAuthenticationScheme() !==
      AuthenticationScheme.DEPSSO
    ) {
      return;
    }

    void (async () => {
      if (!location.pathname.includes('/auth/callback')) {
        return;
      }
      if (!location.search) {
        return;
      }

      const state = new URLSearchParams(location.search).get('state');
      if (!state) {
        return;
      }

      if (processedStates.has(state)) {
        return;
      }
      processedStates.add(state);

      const storedState = await userManager.settings.stateStore.get(state);
      if (!storedState) {
        return;
      }

      const storedStateAsJson = JSON.parse(storedState) as {
        data?: { action?: string };
      };

      const action = storedStateAsJson?.data?.action;

      if (action === 'signin') {
        try {
          await userManager.signinCallback();

          props.actionHooks.onEndLogin();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      }

      if (action === 'signout') {
        try {
          await userManager.signoutCallback();

          props.authStoreAccess.clearAuthState();

          props.actionHooks.onEndLogout();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      }

      location.href = '/';
    })();
  }, [userManager, props]);
};

export const useDepSsoAuthScheme = (props: {
  authStoreAccess: AuthStoreAccess;
  actionHooks: AuthActionHooks;
}): IAuthScheme => {
  const isEnabled = useMemo(
    () => import.meta.env.VITE_AUTH_OIDC_ENABLED === 'true',
    [],
  );

  const userManager = useMemo(() => getUserManager(), []);

  useEffect(() => {
    return () => {
      userManager.stopSilentRenew();
    };
  }, [userManager]);

  const login = useCallback(async () => {
    if (props.authStoreAccess.getAuthenticationScheme()) {
      return;
    }

    props.actionHooks.onStartLogin();

    props.authStoreAccess.resetForAuthenticationScheme(
      AuthenticationScheme.DEPSSO,
    );

    try {
      await userManager.signinRedirect({ state: { action: 'signin' } });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);

      props.authStoreAccess.clearAuthState();
    }
  }, [props]);

  const logout = useCallback(async () => {
    if (
      props.authStoreAccess.getAuthenticationScheme() !==
      AuthenticationScheme.DEPSSO
    ) {
      return;
    }

    props.actionHooks.onStartLogout();

    await userManager.signoutRedirect({ state: { action: 'signout' } });
  }, [props]);

  const refresh = useCallback(async () => {
    // use getAuthenticationScheme() to avoid rerender
    if (
      props.authStoreAccess.getAuthenticationScheme() !==
      AuthenticationScheme.DEPSSO
    ) {
      return;
    }

    props.actionHooks.onStartLogin();

    const newUser = await userManager.signinSilent();

    if (newUser) {
      props.actionHooks.onEndLogin();

      return;
    }

    props.actionHooks.onStartLogout();

    props.authStoreAccess.clearAuthState();

    props.actionHooks.onEndLogout();
  }, [props, userManager]);

  const syncJwtTokenFromOidcUser = useCallback(async () => {
    if (useDebugLogging) {
      // eslint-disable-next-line no-console
      console.log('syncing jwt token from oidc user');
    }

    const jwtTokens = getJwtTokensFromOidcUser(
      (await userManager.getUser()) ?? undefined,
    );

    const accessToken = (() => {
      if (!jwtTokens?.accessToken) {
        return undefined;
      }

      const decodedAccessToken = (() => {
        try {
          return JSON.parse(atob(jwtTokens.accessToken.split('.')[1]!)) as {
            exp: number;
          };
        } catch (_error) {
          return undefined;
        }
      })();
      if (!decodedAccessToken) {
        return undefined;
      }

      const isExpired = +decodedAccessToken.exp <= +Date.now() / 1000;

      if (isExpired) {
        return undefined;
      }

      return jwtTokens.accessToken;
    })();

    const refreshToken = jwtTokens?.refreshToken;

    if (!accessToken || !refreshToken) {
      if (useDebugLogging) {
        // eslint-disable-next-line no-console
        console.log('no jwt tokens found in oidc user');
      }

      return;
    }

    if (useDebugLogging) {
      // eslint-disable-next-line no-console
      console.log(`Generating new token using existing token: ${jwtTokens}`);
    }

    const newJwtToken =
      await getSimaasSchemeAuthenticationJwtTokenUsingAccessToken(
        accessToken,
        refreshToken,
      );
    if (!newJwtToken) {
      if (useDebugLogging) {
        // eslint-disable-next-line no-console
        console.error('Failed to generate new JWT token from OIDC user tokens');
      }
    }

    props.authStoreAccess.setJwtToken(newJwtToken);

    if (useDebugLogging) {
      // eslint-disable-next-line no-console
      console.log(`Generated new token: ${jwtTokens}`);
    }
  }, [props, userManager]);

  const authenticationScheme = props.authStoreAccess.useAuthenticationScheme();

  useEffect(() => {
    if (authenticationScheme !== AuthenticationScheme.DEPSSO) {
      return;
    }

    userManager.events.addUserLoaded(syncJwtTokenFromOidcUser);
    userManager.events.addUserUnloaded(syncJwtTokenFromOidcUser);

    let prevAccessToken: string | undefined = undefined;

    const tokenRenewalInterval = setInterval(async () => {
      const user = await userManager.getUser();

      const accessToken = user?.access_token;
      if (!accessToken) {
        return;
      }

      // initialise prevAccessToken
      if (!prevAccessToken) {
        prevAccessToken = accessToken;

        return;
      }

      // sync only if there is a change from a defined accessToken
      if (accessToken !== prevAccessToken) {
        prevAccessToken = user?.access_token;

        await syncJwtTokenFromOidcUser();
      }
    }, 1000);

    // initialisation of store based on User
    void (async () => {
      const user = await userManager.getUser();
      if (!user) {
        if (useDebugLogging) {
          // eslint-disable-next-line no-console
          console.log(
            'No user found, skipping initial token sync / silent signin',
          );
        }

        return;
      }

      if (!user.expired) {
        if (useDebugLogging) {
          // eslint-disable-next-line no-console
          console.log(
            'User token is still valid, syncing jwt token from oidc user',
          );
        }

        await syncJwtTokenFromOidcUser();

        return;
      }

      if (useDebugLogging) {
        // eslint-disable-next-line no-console
        console.log('Trying to silently sign in user using refresh token');
      }

      await userManager.signinSilent();
    })();

    return () => {
      userManager.events.removeUserLoaded(syncJwtTokenFromOidcUser);
      userManager.events.removeUserUnloaded(syncJwtTokenFromOidcUser);

      clearInterval(tokenRenewalInterval);
    };
  }, [authenticationScheme, userManager]);

  useDepSsoCallbackHandling(userManager, props);

  return useMemo(
    () => ({ isEnabled, login, logout, refresh }),
    [isEnabled, login, logout, refresh],
  );
};
